export const normalizeActionTokenPromptDisplayText = text =>
    String(text || '')
        .replace(/\uFFFC/g, '')
        .replace(/[\u00ad\u200b]/g, '')
        .replace(/\[\[\s*([^\]]*?)\s*\]\]/g, (_match, label) => `[[${String(label).trim()}]]`)
        .replace(/\s+/g, ' ')
        .trim()

// Read-only answers render short action prompts as the inline pill, but past this size the
// pill's per-line capsule fragments (box-decoration-break) turn a paragraph-scale prompt into
// visual noise, so the token switches to the block "generation card" treatment: label header +
// a prompt preview clamped to ACTION_TOKEN_CARD_PROMPT_CLAMP_LINES, full prompt behind the
// existing click popover.
export const ACTION_TOKEN_CARD_PROMPT_MIN_CHARS = 160
export const ACTION_TOKEN_CARD_PROMPT_CLAMP_LINES = 4
// Display/estimation form of the clamp: ~clamp-lines worth of characters at the estimator's
// 72 chars/line wrap model (answerPlaceholderHeight BUCKETS_WRAP_CHARS_PER_LINE), so collapse
// decisions and reserved heights track what the card actually renders.
export const ACTION_TOKEN_CARD_PROMPT_CLAMP_CHARS = 300

export const actionTokenPromptUsesCard = prompt => {
    const text = typeof prompt === 'string' ? prompt.trim() : ''
    if (!text) return false
    return text.length > ACTION_TOKEN_CARD_PROMPT_MIN_CHARS || text.includes('\n')
}

// The card collapses the prompt's own newlines into flowing text (CSS white-space: normal)
// and line-clamps it, so the text form contributes a single clamped paragraph.
export const clampActionTokenPromptForDisplay = prompt => {
    const text = typeof prompt === 'string' ? prompt.trim() : ''
    if (!text || !actionTokenPromptUsesCard(text)) return text

    const flattened = text.replace(/\s+/g, ' ').trim()
    if (flattened.length <= ACTION_TOKEN_CARD_PROMPT_CLAMP_CHARS) return flattened
    return `${flattened.slice(0, ACTION_TOKEN_CARD_PROMPT_CLAMP_CHARS).trimEnd()}\u2026`
}

const BLOCK_TEXT_BOUNDARY_NODES = new Set([
    'ADDRESS',
    'ARTICLE',
    'ASIDE',
    'BLOCKQUOTE',
    'DIV',
    'H1',
    'H2',
    'H3',
    'H4',
    'H5',
    'H6',
    'LI',
    'P',
    'PRE',
    'SECTION',
])

const textContentWithBlockBoundaries = root => {
    const parts = []

    const appendBoundary = () => {
        const lastPart = parts[parts.length - 1]
        if (lastPart && !lastPart.endsWith('\n')) parts.push('\n')
    }

    const appendNode = node => {
        if (node?.nodeType === 3) {
            parts.push(node.nodeValue || '')
            return
        }

        const nodeName = node?.nodeName?.toUpperCase?.() || ''
        if (nodeName === 'BR') {
            appendBoundary()
            return
        }

        Array.from(node?.childNodes || []).forEach(appendNode)
        if (BLOCK_TEXT_BOUNDARY_NODES.has(nodeName)) appendBoundary()
    }

    appendNode(root)
    return parts.join('').replace(/\n+$/, '')
}

const rangeTextContent = range =>
    typeof range?.cloneContents === 'function'
        ? textContentWithBlockBoundaries(range.cloneContents())
        : range?.toString?.() || ''

export const actionTokenPromptMatchesPriorContent = (prompt, priorContent) => {
    const normalizedPrompt = normalizeActionTokenPromptDisplayText(prompt)
    if (!normalizedPrompt) return false

    return normalizedPrompt === normalizeActionTokenPromptDisplayText(priorContent)
}

export const actionTokenPromptMatchesAdjacentContent = (prompt, priorContent, followingContent) =>
    actionTokenPromptMatchesPriorContent(prompt, priorContent) ||
    actionTokenPromptMatchesPriorContent(prompt, followingContent)

export const textContentBeforeElement = (root, element) => {
    if (!root || !element?.ownerDocument || !root.contains?.(element)) return ''

    const range = element.ownerDocument.createRange()
    range.selectNodeContents(root)
    range.setEndBefore(element)
    const text = rangeTextContent(range)
    range.detach?.()
    return text
}

export const textContentAfterElement = (root, element) => {
    if (!root || !element?.ownerDocument || !root.contains?.(element)) return ''

    const editorRoot = element.closest?.('.ProseMirror')
    const rangeRoot = editorRoot && root.contains(editorRoot) ? editorRoot : root
    const range = element.ownerDocument.createRange()
    range.selectNodeContents(rangeRoot)
    range.setStartAfter(element)
    const text = rangeTextContent(range)
    range.detach?.()
    return text
}
